import { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "../hooks/useAuthStore";
import {
  Car,
  Users,
  Building2,
  MapPin,
  ShieldCheck,
  CalendarDays,
  ArrowRight,
  HandCoins,
} from "lucide-react";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Testimonials from "../components/Testimonials";
import FeaturedDestinationCarousel from "../components/FeaturedDestinationCarousel";

const services = [
  {
    title: "Reliable Rides, Anytime",
    cta: "Show more",
    to: "/rides",
    image: "/images/howitworks1.jpg",
  },
  {
    title: "Pulo assistants that elevate your journey",
    cta: "Find a guide",
    to: "/travel-assistant",
    image: "/images/howitworks2.jpg",
  },
  {
    title: "Book a home outside your home with Pulo",
    cta: "Book now",
    to: "/stays",
    image: "/images/howitworks3.jpg",
  },
  {
    title: "Earn with Pulo",
    cta: "Provide service",
    to: "/auth/welcome",
    image: "/images/howitworks4.jpg",
  },
];

const choosePulo = [
  {
    title: "Pulo Assistant",
    description: "Local experts who bring destinations to life.",
    icon: Users,
    to: "/guides",
  },
  {
    title: "Rides",
    description: "Safe, reliable, and flexible transport options.",
    icon: Car,
    to: "/rides",
  },
  {
    title: "Stay",
    description: "Verified accommodations for every traveler.",
    icon: Building2,
    to: "/stays",
  },
];

const reassurance = [
  {
    title: "Local Knowledge",
    description: "Handpicked guides who know every hidden spot.",
    icon: MapPin,
  },
  {
    title: "Your Safety, Our Priority",
    description: "Verified drivers, guides, and hosts.",
    icon: ShieldCheck,
  },
  {
    title: "No Surprises",
    description: "Upfront pricing, no hidden fees.",
    icon: HandCoins,
  },
  {
    title: "Flexible Stays",
    description: "Short-term or long-term rentals for any trip.",
    icon: CalendarDays,
  },
];

function Home() {
  const navigate = useNavigate();
  const { isAuthenticated, role } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      if (role === "admin" || role === "super_admin") {
        navigate("/admin", { replace: true });
      } else if (role === "assistant" || role === "serviceProvider") {
        navigate("/dashboards/assistant/overview", { replace: true });
      } else {
        navigate("/user/travel-assistant", { replace: true });
      }
    }
  }, [isAuthenticated, role, navigate]);

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <main className="space-y-8 md:space-y-20">
        <section className="relative w-full min-h-[20vh] sm:min-h-[50vh] xl:min-h-[85vh] flex items-center justify-center px-4 max-sm:py-6">
          <div className="absolute inset-0">
            <img
              src="/images/home-hero.jpg"
              alt="Traveler with backpack"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="relative z-10 max-w-2xl w-full text-center text-white flex flex-col items-center gap-6">
            <div className="space-y-4">
              <h1 className="text-2xl sm:text-5xl font-bold leading-">
                Your Personal Travel Companion, Anywhere.
              </h1>
              <p className="text-xs sm:text-lg font-medium">
                Book rides, guides and stays — all in one app. Travel stress
                free with local experts at your side.
              </p>
            </div>

          </div>
        </section>

        <section className="max-w-[80%] mx-auto">
          <div className="text-center mb-3 md:mb-12 space-y-3">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#570033]">
              How It Works
            </h2>
            <p className="text-sm text-neutral-900 font-medium">
              One app, multiple use
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {services.map((service) => (
              <article
                key={service.title}
                className="relative rounded-2xl overflow-hidden min-h-[260px] h-[22rem] md:min-h-[360px] md:h-[30rem] shadow-lg flex flex-col"
              >
                <div className="absolute inset-0">
                  {service.imageClass ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={service.image}
                        alt={service.title}
                        className={`w-full h-full ${service.imageClass}`}
                      />
                    </div>
                  ) : (
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-right"
                    />
                  )}
                </div>

                <div className="absolute inset-0 bg-black/15" />

                <div className="relative z-10 flex flex-col p-6 md:p-8 text-white mt-auto space-y-2">
                  <h3 className="text-2xl md:text-xl font-bold">
                    {service.title}
                  </h3>

                  <NavLink
                    to={service.to}
                    className="max-sm:hidden text-sm self-start inline-flex items-center gap-2 bg-[#61013B] text-white font-medium px-4 py-3 rounded-lg no-underline"
                  >
                    {service.cta}
                  </NavLink>

                  <NavLink
                    to={service.to}
                    className="sm:hidden flex items-center gap-2 text-white"
                  >
                    <div>
                      <span className="relative inline-flex flex-col">
                        <span className="font-bold">Show more</span>
                        <span className="h-[2px] w-full bg-white" />
                      </span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white" />
                  </NavLink>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Why pulo*/}
        <section className="max-w-[80%] mx-auto space-y-8">
          <div className="text-center  space-y-4 max-w-2xl mx-auto">
            <h2 className="text-2xl font-semibold text-[#570033]">
              Why Choose Pulo?
            </h2>
            <p className="text-[#2B333C] text-sm font-medium">
              Our crew is constantly growing, but we all work toward one goal:
              to make travel simple, seamless, and stress-free for users
              worldwide.
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-3">
            {choosePulo.map(({ title, description, icon: Icon, to }, index) => (
              <NavLink
                key={title}
                to={to}
                className={`rounded-[20px] sm:rounded-[24px] h-full flex flex-col items-center justify-center text-white py-6 px-4 sm:py-8 gap-3 no-underline bg-[#61013B] hover:bg-[#7a0051] transition-colors ${
                  index === 0 ? "col-span-2 md:col-span-1" : ""
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg sm:text-xl font-bold">{title}</h3>
                  </div>

                  <p className="text-sm sm:text-lg text-white text-center font-medium leading-relaxed">
                    {description}
                  </p>
                </div>
              </NavLink>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 ">
            {reassurance.map(({ title, description, icon: Icon }) => (
              <div
                key={title}
                className="flex gap-4 rounded-2xl p-4 max-sm:border max-sm:border-gray-200 max-sm:shadow-md "
              >
                <div className="h-12 w-12 rounded-full max-sm:bg-[#EFE6EB]  text-[#7a0051] flex items-center justify-center">
                  <Icon className="h-6 w-6 " />
                </div>
                <div className="sm:space-y-3">
                  <p className="font-bold sm:text-lg text-[#570033]">
                    {title}:
                  </p>
                  <p className="text-sm text-[2B333C] font-medium">
                    {description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-12">
            <NavLink
              to="/about"
              className="inline-flex w-full sm:w-auto px-12 py-3 rounded-md bg-[#570033] text-white font-semibold no-underline hover:bg-[#3f0125] justify-center"
            >
              Explore Now
            </NavLink>
          </div>
        </section>

        {/* Destinations*/}
        <section className="max-w-[80%] mx-auto space-y-8">
          <FeaturedDestinationCarousel
            title="Featured Destinations"
            subtitle="Explore Top Cities with Local Experts..."
            items={[
              {
                title: "Maiduguri",
                subtitle: "Landmark, great meals and rich culture.",
                src: "/images/maiduguri.jpg",
                slug: "maiduguri",
              },
              {
                title: "Lagos",
                subtitle: "Beaches, nightlife and city energy.",
                src: "/images/lagos.jpg",
                slug: "lagos",
              },
              {
                title: "Calabar",
                subtitle: "Culture and nature escapes.",
                src: "/images/calabar.jpg",
                slug: "calabar",
              },
            ]}
            onItemClick={(item) =>
              navigate(`/explore/${item.slug || ""}`, { state: item })
            }
          />
        </section>

        <section className=" w-[92%] max-w-5xl mx-auto text-center">
          <h2 className="text-2xl font-semibold text-[#570033]">
            Earn with Pulo
          </h2>
          <p className="text-[#2B333C] mt-4 font-medium leading-relaxed">
            Love meeting new people and sharing amazing stories? Join us as a
            Pulo Assistant and turn your passion for travel into an
            adventure-filled career. Connect with travelers from all over the
            world.
          </p>
          <div className="flex  items-center justify-center gap-4 mt-6 md:mt-8">
            <NavLink
              to="/auth/welcome"
              className="sm:px-16 py-3 rounded-lg border border-[#570033] bg-[#570033] text-white font-medium no-underline w-full sm:w-auto text-center"
            >
              Join Pulo
            </NavLink>
            <NavLink
              to="/auth/login"
              className="sm:px-16 py-3 rounded-lg border border-[#570033]  text-[#570033] font-medium no-underline w-full sm:w-auto text-center"
            >
              Log in
            </NavLink>
          </div>
        </section>

        <Testimonials />
      </main>
      <Footer />
    </div>
  );
}

export default Home;
